import React, {useRef, useState} from "react";
import {connect} from "react-redux";
import * as fileExplorerApi from "../../../../../api/fileExplorerApi";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, Fade,
  FormControlLabel,
  IconButton,
  TextField
} from "@mui/material";
import { withStyles } from "@mui/styles";
import {BASE_URL} from "../../../../../baseUrls";
import {CheckIcon, CopyIcon, OpenIcon} from "../../../../../helpers/Icons";
import InfoText from "../../../../util/common/InfoText";
import Keyword from "../../../../util/common/Keyword";
import KeywordSm from "../../../../util/common/KeywordSm";
import Chip from "@mui/material/Chip";
import {updateItemSharing} from "../../../../../state/actions/updateDataActions";
import LoadingCircle from "../../../../util/common/LoadingCircle";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";

const styles = {
  root:{
    display:'flex',
    flexDirection:'column',
    gap:'0.5em',
    padding:'0.5em'
  },
  checkbox:{
    alignItems:'start',
    height:'1em',
    paddingBottom: 0,
    verticalAlign: 'text-top',
  },
  toolbarButton: {
    padding: '5.1px 10.2px',
    fontSize: '0.7225rem',
    height: '34px',
    color: 'rgba(0, 0, 0, 0.87)',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    '& span:first-child':{
      marginLeft: '-3.4px',
      marginRight: '6.8px'
    },
    '& svg':{
      width: '1em',
      height: '1em',
    }
  },
  shareRow:{
    display:'flex',
    gap:'0.5rem',
    flexWrap:'wrap',
    overflowX:'auto',
  },
  shareLink:{
    flex:1,
    display:'flex',
    border:'1px solid rgba(0,0,0,0.27)',
    borderRadius:'4px',
    alignItems: 'center',
    padding:'0 3px',
    maxWidth:'600px',
    position:'relative'
  },
  linkTextWrap:{
    display:'flex',
    padding:'0 3px',
    width:0,
    flex:1,
    minWidth:'5em',
  },
  linkTextPre:{
    overflow:'hidden',
    textOverflow:'ellipsis',
    whiteSpace:'nowrap',
    padding:'0',
    flexShrink:1,
    minWidth:'2.5em',
  },
  linkText:{
    overflow:'hidden',
    textOverflow:'ellipsis',
    whiteSpace:'nowrap',
    flex: 1,
    padding:'0 3px',
    width:0,
    minWidth:'9em',
    lineHeight:'1.75em',
    border: 'none',
    outline: 'none'

  },
  copiedSuccess:{
    position:'absolute',
    right:'38px',
    backgroundColor:'#FFF',
  },
  emailForm:{
    position:'relative',
    overflow:'hidden'
  },
  emailLoader:{
    position:'absolute',
    top:'calc(50% - 45px)',
    left:'50%',
  },
  emailSent:{
    position:'absolute',
    top:'calc(50% - 45px)',
    left:'50%',
    backgroundColor: "#FFF"
  },
}

async function copyTextToClipboard(text) {
  if ('clipboard' in navigator) {
    return await navigator.clipboard.writeText(text);
  } else if ('execCommand' in document){
    return document.execCommand('copy', true, text);
  }
}

function ItemShareForm(props) {
  const { user, viewItem, closeShare, dispatchUpdate = true, classes } = props;
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef(null);
  const sentFadeTimeout = useRef(null);
  const errorFadeTimeout = useRef(null);
  const isShared = !!viewItem.share_hash;
  const shareUrl = `${BASE_URL}/v2/view/item/${viewItem.share_hash}`;
  const loading = (sending || updating);

  const handleShare = () => {
    setSending(true);
    fileExplorerApi.shareFileByEmail({
        mindfileId: viewItem.mindfile_id,
        itemId: viewItem.id,
        authenticityToken: user.authenticity_token,
        to: to,
        message: message,
      })
      .then((json) => {
        if(json.status === "OK") {
          setTo("");
          setMessage("");
          setSending(false);
          setEmailSent(true);
        }else{
          setSending(false);
          setEmailError(true);
        }
      })
      .catch(e => {
        setSending(false);
        setEmailError(true);
        console.error(e);
      });
  };
  const handleUpdateSharing = (event) => {
    if(isShared){
      confirmStopSharing();
    }else{
      doUpdateSharing(!isShared);
    }
  };

  const doUpdateSharing = (sharing, opts) => {
    if(updating){
      return;
    }
    setUpdating(true);

    const shareOpt = !!opts ? opts : viewItem.share_option;
    props.updateItemSharing(viewItem.mindfile_id, viewItem.id, sharing, shareOpt, (newItem) => {
      setUpdating(false);
    }, dispatchUpdate);
  };

  const confirmStopSharing = (event) => {
    if(updating){
      return;
    }
    setConfirmOpen(true);
  };

  const validateEmail = email => {
    const re = /^((([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))(, *)?)+$/;
    return re.test(String(email).toLowerCase());
  }

  const shareForm = () => (
    <div style={{display:'flex', flexDirection:'column', position:'relative'}}>
      <div>
        <FormControlLabel
          id="enableSharing"
          control={<Checkbox id="enableSharingCB" checked={isShared} onClick={handleUpdateSharing} disabled={updating.current} />}
          label={<KeywordSm>Enable Sharing</KeywordSm>}
        />
      </div>
      {isShared ? <>
          <KeywordSm>Share Link</KeywordSm>
          <div className={classes.shareRow}>
            <div className={classes.shareLink}>
              <input id="itemShareLink" type="text" className={classes.linkText} value={shareUrl} readOnly />
              <Fade in={copied} timeout={100} onEnter={() => {
                if(copiedTimeout.current){
                  clearTimeout(copiedTimeout.current);
                }
                copiedTimeout.current = setTimeout(() => setCopied(false), 3000);
              }}>
                <Chip id="copiedSuccess" className={classes.copiedSuccess} label="Copied" variant="outlined" color="primary" size="small" deleteIcon={<CheckIcon />} />
              </Fade>
              {/* todo: icon small */}
              <IconButton id="copyLink" size="small" onClick={() => {
                copyTextToClipboard(shareUrl)
                .then(() => setCopied(true))
                .catch(console.log);
              }}>
                <CopyIcon size="small"/>
              </IconButton>
            </div>
            <Button
              id="viewSharePage"
              className={classes.toolbarButton}
              variant="outlined"
              startIcon={<OpenIcon />}
              onClick={() => window.open(shareUrl, "_blank")}
            >
              View
            </Button>
          </div>
          <InfoText>
            Anyone with this link can view this item.
          </InfoText>
          <InfoText>
            <Keyword>Disable Sharing</Keyword> to <Keyword>permanently</Keyword> revoke access with this link.
            If sharing is <Keyword>re-enabled</Keyword> a <Keyword>new link</Keyword> will be generated.
          </InfoText>
          <div className={classes.emailForm} style={{marginTop:'1em'}}>
            <div>
              <InfoText><Keyword>Send link in email</Keyword> (separate multiple email addresses with a comma)</InfoText>
            </div>
            <div>
              <TextField
                id="shareEmailTo"
                label="To" variant="outlined" size="small"
                style={{margin:'0.5rem 0 0'}}
                required fullWidth
                disabled={sending || updating}
                value={to}
                onChange={e => setTo(e.target.value)}
              />
            </div>
            <div>
              <TextField
                id="shareEmailMessage"
                label="Message" variant="outlined" size="small"
                style={{margin:'0.5rem 0'}}
                required fullWidth
                disabled={sending || updating}
                multiline minRows={5}
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </div>
          </div>
        </>
        : null
      }
      <div style={{display:'flex'}}>
        {isShared ?
          <Button
            id="shareEmailSend"
            variant="contained" color="primary"
            style={{ marginRight: 8 }}
            onClick={handleShare}
            disabled={!validateEmail(to) || !message.length || sending || updating}
          >
            Send
          </Button>
          : null
        }
        <Button
          id="shareClose"
          // style={{marginLeft:'auto'}}
          variant="contained" color="secondary"
          onClick={closeShare}>Close</Button>

      </div>
      <Fade in={loading} timeout={100}>
        <div>
          <LoadingCircle className={classes.emailLoader} />
        </div>
      </Fade>
      <Fade in={emailSent} timeout={300} onEnter={() => {
        if(sentFadeTimeout.current){
          clearTimeout(sentFadeTimeout.current);
        }
        sentFadeTimeout.current = setTimeout(() => setEmailSent(false), 3000);
      }}>
        <Alert id="emailSent" className={classes.emailSent} variant="outlined" severity="success">Email Sent</Alert>
      </Fade>
      <Fade in={emailError} timeout={300} onEnter={() => {
        if(errorFadeTimeout.current){
          clearTimeout(sentFadeTimeout.current);
        }
        errorFadeTimeout.current = setTimeout(() => setEmailError(false), 3000);
      }}>
        <Alert id="emailFailed" className={classes.emailSent} variant="outlined" severity="error">Unable to send email.</Alert>
      </Fade>
    </div>
  );

  return (
    <div className={classes.root}>
      {shareForm()}

      <Dialog PaperProps={{id:"disableSharing"}}
        onClose={() => {setConfirmOpen(false)}}
        open={confirmOpen}
      >
        <DialogTitle>
          <div style={{display:'flex', flexWrap:'wrap-reverse', justifyContent:'space-between'}}>
            Disable Sharing
            <IconButton onClick={() => {setConfirmOpen(false)}} style={{width:'2rem', height:'2rem', margin:'-0.5rem 0 0 auto'}}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent id="disableShareInfo">
          <InfoText style={{fontSize: '1em', fontWeight: 300, marginBottom: '1em'}}>
            <Keyword>Disable Sharing</Keyword> will <Keyword>permanently</Keyword> revoke access with the current link.
            If sharing is <Keyword>re-enabled</Keyword> a <Keyword>new link</Keyword> will be generated.
          </InfoText>
          <InfoText style={{fontSize: '1em', fontWeight: 300}}>
            Are you sure you want to disable sharing for: <Keyword style={{textTransform:'none'}}>{viewItem.name}</Keyword>
          </InfoText>
        </DialogContent>
        <DialogActions>
          <Button
            id="disableSharingConfirm"
            variant="contained"
            color="primary"
            onClick={() => {
              doUpdateSharing();
              setConfirmOpen(false);
            }}
          >
            Disable Sharing
          </Button>
          <Button
            id="disableSharingCancel"
            variant="contained"
            color="secondary"
            onClick={() => {setConfirmOpen(false)}}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>);
}

const mapStateToProps = state => ({
  user: state.auth,
});

const mapDispatchToProps = {
  updateItemSharing
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(ItemShareForm));
